import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Save } from "lucide-react";
import { clientAPI } from "@/lib/client-api";
import type { WelcomeSection } from "@/lib/db-api";

const AdminWelcomeSection = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [section, setSection] = useState<WelcomeSection | null>(null);

  useEffect(() => {
    const auth = localStorage.getItem('adminAuth');
    if (auth !== 'true') {
      navigate('/admin/login');
      return;
    }
    loadSection();
  }, [navigate]);

  const loadSection = () => {
    try {
      const data = clientAPI.getWelcomeSection();
      if (data) setSection(data);
    } catch (error) {
      console.error('Error loading welcome section:', error);
    }
  };

  const handleSave = () => {
    if (!section) return;

    try {
      clientAPI.updateWelcomeSection(section);
      toast({
        title: "Success",
        description: "Welcome section updated successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update welcome section",
        variant: "destructive",
      });
    }
  };

  if (!section) return null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Button onClick={() => navigate('/admin/dashboard')} variant="ghost">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
          <a href="/" target="_blank" rel="noopener noreferrer">
            <Button variant="outline">
              View Live Site
            </Button>
          </a>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-3xl">
        <Card>
          <CardHeader>
            <CardTitle>Edit Welcome Section</CardTitle>
            <CardDescription>Update the welcome section content - changes appear immediately on homepage</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 p-4 rounded-lg border mb-6">
              <h4 className="font-semibold mb-3 text-sm uppercase tracking-wide text-muted-foreground">Current Content Preview</h4>
              <div className="space-y-3 bg-background p-4 rounded">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">TITLE</p>
                  <h3 className="text-xl font-bold">{section.title}</h3>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">PARAGRAPH 1</p>
                  <p className="text-sm">{section.paragraph1}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">PARAGRAPH 2</p>
                  <p className="text-sm">{section.paragraph2}</p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={section.title}
                onChange={(e) => setSection({ ...section, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="paragraph1">First Paragraph</Label>
              <Textarea
                id="paragraph1"
                value={section.paragraph1}
                onChange={(e) => setSection({ ...section, paragraph1: e.target.value })}
                rows={4}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="paragraph2">Second Paragraph</Label>
              <Textarea
                id="paragraph2"
                value={section.paragraph2}
                onChange={(e) => setSection({ ...section, paragraph2: e.target.value })}
                rows={4}
              />
            </div>
            <Button onClick={handleSave} className="w-full">
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminWelcomeSection;
